import { encodeQuery } from '~/src/dash/api/resource';
import { createAnalyticsUrl, createUrl, restGet, restPost } from '~/src/dash/api/rest';
import { ReportTypeID } from '~/src/dash/apiV2/reporting/reportTypes';
import { IReportDataRow } from '~/src/dash/views/reporting/Preview/common';

// map of reportType to the URL path for querying Last Update timestamp
const reportTypesToTimestampUpdateAPI: { [key: string]: string } = {
  [ReportTypeID.AuctionReport]: 'last_update_timestamp_by_auctions',
  [ReportTypeID.BidderReport]: 'last_update_timestamp_by_buyers',
  [ReportTypeID.AuctionFillRateReport]: 'last_update_timestamp_by_auctions_fill_rate',
  [ReportTypeID.CampaignReport]: 'last_update_timestamp_by_campaign',
  [ReportTypeID.AudienceSegmentLiverampReport]: 'last_update_timestamp_by_segments',
  [ReportTypeID.SSAIReport]: 'last_update_timestamp_by_ssai',
  [ReportTypeID.UniqueUserReport]: 'last_update_timestamp_by_unique_user_report',
  [ReportTypeID.BrandSafetyTrackerReport]: 'last_update_timestamp_by_brand_safety_tracker',
  [ReportTypeID.DealReport]: 'last_update_timestamp_by_deals',
  [ReportTypeID.OutboundDealReport]: 'last_update_timestamp_by_publica_deals',
  [ReportTypeID.EleaAIScanAllReport]: 'last_update_timestamp_by_recognition_scan_all',
  [ReportTypeID.EleaAIEnrichmentReport]: 'last_update_timestamp_by_recognition_enrichment',
  [ReportTypeID.EleaAILanguageDetectionReport]: 'last_update_timestamp_by_recognition_enrichment',
  [ReportTypeID.InventoryRouteReport]: 'last_update_timestamp_by_inventory_routes',
  [ReportTypeID.DestinationReport]: 'last_update_timestamp_by_inventory_routes',
  [ReportTypeID.ContextualSegmentReport]: 'last_update_timestamp_by_contextual_segments',
  [ReportTypeID.ShareOfVoiceReport]: 'last_update_timestamp_by_share_of_voice',
  [ReportTypeID.PodHourlyReport]: 'last_update_timestamp_by_pod_hourly',
  [ReportTypeID.AdvertiserRuleReport]: 'last_update_timestamp_by_pod_hourly',
  [ReportTypeID.APSReport]: 'last_update_timestamp_by_buyers_aps',
  [ReportTypeID.AuctionQuickReport]: 'last_update_timestamp_by_auctions',
  [ReportTypeID.BidderQuickReport]: 'last_update_timestamp_by_buyers',
  [ReportTypeID.ContentQuickReport]: 'last_update_timestamp_by_buyers',
  [ReportTypeID.AdomainQuickReport]: 'last_update_timestamp_by_buyers',
  [ReportTypeID.AppBundleQuickReport]: 'last_update_timestamp_by_buyers',
  [ReportTypeID.CampaignQuickReport]: 'last_update_timestamp_by_campaign',
};

// retrieve the last updated timestamp
export async function getReportLastUpdateTimestamp(
  publisherID: string,
  reportType: ReportTypeID,
): Promise<number | null> {
  if (!reportTypesToTimestampUpdateAPI[reportType]) return null;

  const reqUrl = createAnalyticsUrl(reportTypesToTimestampUpdateAPI[reportType], publisherID, 'v1');

  try {
    const response = await restGet<any>(reqUrl);
    return response?.parsedBody?.LastUpdateTimestamp;
  } catch (err) {
    throw err;
  }
}

export interface IPreviewReport {
  data: Array<IReportDataRow>;
  rawQuery: string;
}

// retrieve preview report data
export async function fetchReportPreviewData(params: {
  [key: string]: any;
}): Promise<IPreviewReport> {
  const reqUrl = `${createAnalyticsUrl('discovery/preview_report')}&${encodeQuery(params)}`;

  try {
    const response = await restPost<any>(reqUrl, params);
    const { parsedBody } = response;
    return {
      data: parsedBody.data || [],
      rawQuery: parsedBody.RawQuery,
    };
  } catch (err) {
    throw err;
  }
}

// retrieve preview report data for IAS reports
export async function fetchIASReportPreviewData(
  publisherID: string,
  params: {
    [key: string]: any;
  },
): Promise<IPreviewReport> {
  const reqUrl = `${createUrl('/ias_first_and_third_party_data', publisherID)}&${encodeQuery(
    params,
  )}`;

  try {
    const response = await restPost<any>(reqUrl, params);
    return {
      data: response?.parsedBody?.rows || [],
      rawQuery: '',
    };
  } catch (err) {
    throw err;
  }
}
